'use client';

import * as React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowRight } from 'lucide-react';

import { routes } from '@workspace/routes';
import { Button } from '@workspace/ui/components/button';
import { Logo } from '@workspace/ui/components/logo';

import { MobileMenu } from '~/components/mobile-menu';
import { ProductNavigation } from '~/components/product-navigation';
import { ResourcesNavigation } from '~/components/resources-navigation';
import { HERO_V3 } from '~/lib/landing-content';
import {
  isMarketingRouteActive,
  SHOW_PRODUCT_AND_RESOURCES
} from '~/lib/marketing-navigation';

/** Outcome-led product navigation, direct MCP access, and one signup CTA. */
export function Navbar(): React.JSX.Element {
  const pathname = usePathname();
  const scrollToSection = React.useCallback(() => {
    const section =
      document.getElementById('product') ??
      document.getElementById('use-case-preview');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  React.useEffect(() => {
    if (window.location.hash === '#product') {
      const timer = setTimeout(scrollToSection, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollToSection]);

  return (
    <section className="marketing-header sticky inset-x-0 top-0 z-40 flex items-center border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="w-full px-6 md:px-8 lg:px-12 xl:px-16 mx-auto max-w-[1320px]">
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex justify-between items-center">
          {/* Left: Logo. Mark + wordmark while the bevel L is still building
              recognition; brand rule 5A (wordmark only) is the eventual target. */}
          <Link
            href={routes.marketing.Index}
            className="marketing-focus flex items-center gap-2 rounded-sm"
          >
            <Logo className="h-7 w-auto text-[20px]" />
          </Link>

          {/* Right: CTAs */}
          <div className="flex items-center gap-6">
            {SHOW_PRODUCT_AND_RESOURCES && <ProductNavigation />}
            <Link
              href={routes.marketing.Pricing}
              aria-current={
                isMarketingRouteActive(pathname, '/pricing')
                  ? 'page'
                  : undefined
              }
              className="marketing-nav-link rounded-sm text-sm font-medium"
            >
              Pricing
            </Link>
            {SHOW_PRODUCT_AND_RESOURCES && <ResourcesNavigation />}
            <Link
              href="/mcp"
              aria-current={
                isMarketingRouteActive(pathname, '/mcp') ? 'page' : undefined
              }
              className="marketing-nav-link rounded-sm text-sm font-medium"
            >
              MCP
            </Link>
            <Link
              href={routes.dashboard.auth.SignIn}
              className="marketing-nav-link rounded-sm text-sm font-medium"
            >
              Sign In
            </Link>
            <Button
              asChild
              variant="default"
              size="default"
              className="marketing-cta"
            >
              <Link href={routes.dashboard.auth.SignUp}>
                {HERO_V3.cta}
                <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
              </Link>
            </Button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <MobileMenu className="lg:hidden" />
      </div>
    </section>
  );
}
